/**
 * One brand's admin API (/admin/v1), as the console sees it. Credentials are cookies on the API
 * host (set by the sign-in routes), so every call is `credentials: "include"` and no token ever
 * reaches this code. An expired access cookie is refreshed once, transparently.
 */

export type Brand = (typeof __CONSOLE_BRANDS__)[number];

export const BRANDS: Brand[] = __CONSOLE_BRANDS__;

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, readonly body: Record<string, unknown>) {
    super(code);
  }
}

export class SignedOut extends Error {
  constructor() {
    super("signed_out");
  }
}

export class AdminClient {
  private refreshing: Promise<boolean> | null = null;

  constructor(readonly brand: Brand) {}

  /** JSON in and out; 401 → one refresh → retry; 401 again → SignedOut. */
  async call<T>(method: string, path: string, options: { body?: unknown; revision?: number; retried?: boolean } = {}): Promise<T> {
    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers["Content-Type"] = "application/json";
    if (options.revision !== undefined) headers["If-Match"] = `"${options.revision}"`;
    const res = await fetch(`${this.brand.apiBase}${path}`, {
      method, headers, credentials: "include",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
    if (res.status === 401 && !options.retried && !path.startsWith("/admin/v1/session/")) {
      if (await this.refresh()) return this.call<T>(method, path, { ...options, retried: true });
      throw new SignedOut();
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const data = (text ? JSON.parse(text) : {}) as Record<string, unknown>;
    if (!res.ok) {
      if (res.status === 401) throw new SignedOut();
      throw new ApiError(res.status, String(data["error"] ?? res.status), data);
    }
    return data as T;
  }

  /** One refresh at a time: parallel calls that all saw a 401 share it. */
  private refresh(): Promise<boolean> {
    this.refreshing ??= fetch(`${this.brand.apiBase}/admin/v1/session/refresh`, { method: "POST", credentials: "include" })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => { this.refreshing = null; });
    return this.refreshing;
  }
}
